/* eslint-disable max-len */
import OgImage from '@propertypal/shared/src/resources/images/prime/prime-og-image.png';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { Property } from '@propertypal/shared/src/types';
import {
  PrimeFaqProps,
  ListingTier,
  PrimePropertyStatus,
  PrimePricingResponse,
} from '@propertypal/shared/src/types/prime';
import platform from '@propertypal/shared/src/utils/platform';
import getPrimeFaqs from '@propertypal/shared/src/utils/prime/getPrimeFaqs';
import PrimeFaqs from '@propertypal/web-ui/src/prime/faq/PrimeFaqs';
import PrimeHeader from '@propertypal/web-ui/src/prime/landing/PrimeHeader';
import { PrimeHeaderContainer } from '@propertypal/web-ui/src/prime/landing/PrimeHeader.style';
import PrimeOverlay from '@propertypal/web-ui/src/prime/landing/PrimeOverlay';
import PrimeTiers from '@propertypal/web-ui/src/prime/landing/PrimeTiers';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import React, { FunctionComponent } from 'react';
import generateMetaTags from '../components/layout/MetaTags';
import IMAGE_LOCATOR from '../constants/imageLocator';

const metaData = {
  title: `Prime | Promote Your Property | ${platform.title}`,
  description: `Put your Property in a Prime position. Prime is designed to give properties greater visibility on the ${platform.title} websites.`,
  url: `/prime`,
  image: {
    url: OgImage.src,
    width: 1200,
    height: 630,
  },
};

interface Props extends PrimeFaqProps {
  property: Property | null;
  products: PrimePropertyStatus | null;
  current: ListingTier | null;
  primePricing: PrimePricingResponse;
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  let property: Property | null = null;
  let products: PrimePropertyStatus | null = null;
  let primePricing: PrimePricingResponse | null = null;

  if (ctx?.query?.propertyId) {
    try {
      const result = await axios<Property>(
        {
          method: 'GET',
          url: `${API_URL}/property/${
            parseInt(ctx.query.propertyId as string, 10) - 1200
          }?imageLocator=${encodeURIComponent(IMAGE_LOCATOR)}`,
        },
        undefined,
        ctx,
      );

      property = result.data;

      if (['SILVER', 'BRONZE'].includes(property?.listingTier)) {
        const primeProducts = await axios<PrimePropertyStatus>(
          {
            method: 'GET',
            url: `${API_URL}/premium/status/property/${result.data.id}`,
          },
          undefined,
          ctx,
        );

        products = primeProducts.data;
      }
    } catch (e: any) {
      // silent error
    }
  }

  if (!products) {
    const result = await axios<PrimePricingResponse>(
      {
        method: 'GET',
        url: `${API_URL}/premium/pricing/prime`,
      },
      undefined,
      ctx,
    );
    primePricing = result.data;
  }

  const { allowMultipleExpanded, faqs } = await getPrimeFaqs();

  return {
    props: {
      property,
      products,
      allowMultipleExpanded,
      faqs,
      current: property?.listingTier || null,
      primePricing,
    },
  };
};

const Prime: FunctionComponent<Props> = ({
  property,
  products,
  allowMultipleExpanded,
  faqs,
  current,
  primePricing,
}) => {
  return (
    <>
      <Head>{generateMetaTags(metaData)}</Head>

      <div data-testid="prime-page">
        <PrimeHeaderContainer>
          {current === 'GOLD' && <PrimeOverlay current={current} currentPrimeItem={property?.currentPrimeItem} />}
          <PrimeHeader />
          <PrimeTiers
            property={property}
            current={current}
            products={products?.products}
            daysRemaining={products?.daysRemaining}
            primePricing={primePricing}
          />
        </PrimeHeaderContainer>
        <PrimeFaqs faqs={faqs} allowMultipleExpanded={allowMultipleExpanded} />
      </div>
    </>
  );
};

export default Prime;
